import { useState } from 'react';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import CustomModal from '../../../components/atoms/CustomModal';
import Input from '../../../components/atoms/Input';
import Button from '../../../components/atoms/Button';
import { useTranslation } from 'react-i18next';
import CommissionsSection from './CommissionsSection';
import VatsSection from './VatsSection';
import { useUpdateCommissionMutation, useUpdateVatMutation } from '../../../store/services/commission';
import { message } from 'antd';

export default function CommissionVats() {
  const { t } = useTranslation();
  const [modalType, setModalType] = useState<'commission' | 'vat' | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);

  const [updateCommission, { isLoading: isUpdatingCommission }] = useUpdateCommissionMutation();
  const [updateVat, { isLoading: isUpdatingVat }] = useUpdateVatMutation();

  const handleEditCommission = (record: any) => {
    setSelectedRecord(record);
    setAmount(record.commission?.toString() || '');
    setModalType('commission');
  };

  const handleEditVat = (record: any) => {
    setSelectedRecord(record);
    setAmount(record.vatPercentage?.toString() || '');
    setModalType('vat');
  };

  const handleClose = () => {
    setModalType(null);
    setSelectedRecord(null);
    setAmount('');
    setAmountError(null);
  };

  const handleSubmit = async () => {
    if (!selectedRecord) return;

    if (!amount?.toString().trim()) {
      setAmountError(t('components.requiredField', "Field can't be empty"));
      return;
    }

    try {
      if (modalType === 'commission') {
        await updateCommission({ id: selectedRecord.id, commission: Number(amount) }).unwrap();
      } else {
        await updateVat({ id: selectedRecord.id, vatPercentage: Number(amount) }).unwrap();
      }
      handleClose();
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors ? Object.values(errData.errors)[0] as string : null;
      message.error(errorDetail || errData?.message || 'Error updating');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col gap-1">
        <SectionTitle text={t('home.commission-vats')} />
        <SectionDesc text={t('home.commission-vats-desc')} />
      </div>

      <CommissionsSection onEdit={handleEditCommission} />
      <VatsSection onEdit={handleEditVat} />

      <CustomModal
        isOpen={!!modalType}
        onClose={handleClose}
        title={modalType === 'commission' ? t('home.edit-commission-title') : t('home.edit-vat-title')}
        subTitle={modalType === 'commission' ? t('home.edit-commission-desc') : t('home.edit-vat-desc')}
        width={600}
        closeIconClassName="[&_.ant-modal-close]:!top-8"
      >
        <div className="flex flex-col gap-6 mt-4">
          <Input
            id="amount"
            label={t('home.amount-percentage')}
            placeholder={t('home.enter-amount')}
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setAmountError(null);
            }}
            status={amountError ? 'error' : undefined}
            helperText={amountError || undefined}
            className="!rounded-xl"
            type="number"
          />
          <div className="flex justify-end mt-2">
            <Button 
              variant="dark" 
              onClick={handleSubmit} 
              className="rounded-xl"
              loading={isUpdatingCommission || isUpdatingVat}
            >
              {t('home.done')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
